import { useEffect, useMemo, useRef } from "react";

import { css } from "@compiled/react";
import debounce from "lodash/debounce";
import { FormattedMessage, useIntl } from "react-intl";

import Heading from "@atlaskit/heading";
import { token } from "@atlaskit/tokens";

import {
    useInputValues,
    useInputSetters
} from "@/components/usage-based-pricing-calculator/src/hooks/hooks";
import { wacToken } from "@/libraries/design/foundations";

import { useTeamSizeTracking } from "../hooks/tracking";

import { NumericInput } from "./numeric-input";
import { messages } from "./team-size.messages";

const sectionStyles = css({
    paddingBlock: token("space.400"),
    borderBlockEnd: `1px solid ${wacToken("color.neutral.light300")}`
});

const titleStyles = css({
    fontFamily: wacToken("font.charlie.display"),
    fontWeight: 700,
    fontSize: "20px",
    lineHeight: "26px",
    color: wacToken("color.primary.black"),
    marginBlockEnd: token("space.100")
});

const inputContainerStyles = css({
    display: "flex",
    flexDirection: "column",
    alignItems: "flex-start",
    gap: token("space.100")
});

const labelStyles = css({
    paddingBlockStart: token("space.100"),
    fontFamily: wacToken("font.charlie.text"),
    fontSize: "16px",
    lineHeight: "24px",
    color: wacToken("color.neutral.light700")
});

export const TeamSize = () => {
    const intl = useIntl();
    const { orgUserCount } = useInputValues();
    const { setOrgUserCount } = useInputSetters();
    const { fireTeamSizeChangedEvent } = useTeamSizeTracking();

    const fireTeamSizeChangedEventRef = useRef(fireTeamSizeChangedEvent);
    fireTeamSizeChangedEventRef.current = fireTeamSizeChangedEvent;

    const debouncedFireTeamSizeChangedEvent = useMemo(
        () =>
            debounce((value: number) => {
                fireTeamSizeChangedEventRef.current(value);
            }, 1000),
        []
    );

    useEffect(() => {
        debouncedFireTeamSizeChangedEvent(orgUserCount);
    }, [orgUserCount, debouncedFireTeamSizeChangedEvent]);

    return (
        <section css={sectionStyles}>
            <Heading size="small" as="h2">
                <span css={titleStyles}>
                    <FormattedMessage {...messages.teamSizeTitle} />
                </span>
            </Heading>
            <div css={inputContainerStyles}>
                <label htmlFor="team-size-input" css={labelStyles}>
                    <FormattedMessage {...messages.teamSizeLabel} />
                </label>
                <NumericInput
                    id="team-size-input"
                    min={0}
                    max={100000}
                    value={orgUserCount}
                    onChange={setOrgUserCount}
                    aria-label={intl.formatMessage(messages.teamSizeLabel)}
                />
            </div>
        </section>
    );
};
